import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { StatusMessage } from "../../components/ui/StatusMessage";
import { useAuth } from "../../hooks/useAuth";

export function ForgotPasswordPage() {
  const { isConfigured, resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setSent(false);

    if (!email.trim()) {
      setFormError("Email is required.");
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword(email);
      setSent(true);
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Unable to send password reset instructions.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div>
        <p className="eyebrow">Account recovery</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-normal text-ink">
          Reset your password
        </h1>
        <p className="mt-3 text-base leading-7 text-graphite/70">
          We will send reset instructions if the account exists.
        </p>
      </div>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
        {!isConfigured ? (
          <StatusMessage
            message="Supabase environment variables are missing. Add them before sending reset email."
            tone="error"
          />
        ) : null}

        {sent ? (
          <StatusMessage
            message="Password reset instructions have been sent."
            tone="success"
          />
        ) : null}

        {formError ? <StatusMessage message={formError} tone="error" /> : null}

        <Input
          autoComplete="email"
          label="Email"
          onChange={(event) => setEmail(event.target.value)}
          type="email"
          value={email}
        />

        <Button fullWidth isLoading={isSubmitting} type="submit">
          Send reset link
        </Button>
      </form>

      <p className="mt-6 text-sm text-graphite/70">
        Remembered it?{" "}
        <Link className="link" to="/login">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
